import React from 'react'
import {
  makeStyles,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Grid,
  Typography,
  Alert
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, TypographyProps } from '@paintscout/ui'
import { goToQuote } from 'src/util/routing'

const useStyles = makeStyles<Theme, DetailsItemProps>((theme: Theme) => ({
  root: {
    marginRight: theme.spacing(1.5)
  },
  label: {
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  icon: {
    padding: theme.spacing(1, 0.5)
  },
  value: {
    fontWeight: ({ variant }) => (variant === 'body1' ? 500 : undefined)
  },
  expansionPanelWarning: {
    padding: theme.spacing(1)
  },
  warningLink: {
    marginLeft: theme.spacing(0.75),
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightMedium,
    textDecoration: 'underline'
  }
}))

export interface DetailsItemProps {
  label: string
  expanded?: boolean
  value?: string | number
  detailsComponent?: React.ReactNode
  variant?: TypographyProps['variant']
  expandIcon?: React.ReactNode
  onExpand?: (expanded: boolean) => void
  classes?: StyleClasses<typeof useStyles>
  warningMessage?: { text: string; link?: { text: string; id: string } } | null
}

export default function DetailsItem(props: DetailsItemProps) {
  const { label, value, detailsComponent, variant = 'overline', expandIcon, expanded, warningMessage, onExpand } = props
  const classes = useStyles(props)

  const handleExpand = (event: React.ChangeEvent, expanded: boolean) => {
    if (onExpand) {
      onExpand(expanded)
    }
  }

  return (
    <div className={classes.root}>
      <ExpansionPanel expanded={!!expanded} onChange={handleExpand} disabled={!detailsComponent}>
        <ExpansionPanelSummary
          disabled={!detailsComponent}
          expandIcon={expandIcon}
          classes={{ root: 'expansion-panel-summary', expandIcon: classes.icon }}
          style={{ padding: 0 }}
        >
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant={'overline'} classes={{ root: classes.label }}>
                {label}
              </Typography>
            </Grid>
            {value && !expanded && (
              <Grid item>
                {typeof value === 'string' ? (
                  <Typography className={classes.value} variant={variant}>
                    {value}
                  </Typography>
                ) : (
                  <Typography className={classes.value} variant={variant} value={value} format={'price'} />
                )}
              </Grid>
            )}
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails>
          <Grid container direction="row" justifyContent="flex-end">
            <Grid item xs={12}>
              {detailsComponent}
            </Grid>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
      {warningMessage && (
        <Alert
          className={classes.expansionPanelWarning}
          severity="warning"
          variant="default"
          content={
            warningMessage.link ? (
              <Typography variant="body2">
                {warningMessage.text}
                {warningMessage.link && (
                  <>
                    <a className={classes.warningLink} onClick={() => goToQuote({ id: warningMessage.link.id })}>
                      {warningMessage.link.text}
                    </a>
                  </>
                )}
              </Typography>
            ) : (
              <Typography variant="body2">{warningMessage.text}</Typography>
            )
          }
        />
      )}
    </div>
  )
}
